import { useEffect, useRef, useState } from 'react';
import { useRefreshMutation, useResolveCurrentStore } from '@/entities/user';

/**
 * `POST /auth/refresh` (cookie) → resolve `currentStoreId` from `/me/memberships` (docs/PLAN.md
 * §6: "localStorage / единственное членство / первое" — first membership covers both, since a
 * single membership's "first" is itself), via `useResolveCurrentStore` (also used by `LoginForm`
 * for the same resolution mid-SPA-lifetime). Runs once; returns whether it's finished so
 * `App.tsx` can hold the router behind a full-page spinner until then. `refresh`'s own response
 * already carries the user profile (see authApi.ts's `onQueryStarted`), so there's no separate
 * `GET /me` call here — one is only fired lazily wherever a component actually needs that data.
 */
export function useBootstrap(): boolean {
  const [ready, setReady] = useState(false);
  const startedRef = useRef(false);
  const [refresh] = useRefreshMutation();
  const resolveCurrentStore = useResolveCurrentStore();

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      const result = await refresh()
        .unwrap()
        .catch(() => null);

      if (result) {
        await resolveCurrentStore();
      }

      setReady(true);
    })();
  }, [refresh, resolveCurrentStore]);

  return ready;
}
